import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { adminApi, parseError } from "./api";
import { StatusBadge } from "./ui";

const KINDS = [["storyCategory", "Story categories", "Used by Research in Namma Ooru stories (one per story) and shown as filters on the public stories page."], ["eventType", "Event types", "Used by events (one per event)."], ["researchDomain", "Research domains", "Used by stories and events (several per record)."]];
const usageText = (u) => Object.entries(u).map(([k, n]) => `${n} ${n === 1 ? k.slice(0, -1).replace("storie", "story") : k}`).join(", ");

const Usage = ({ cat }) => {
  const [data, setData] = useState(null);
  return (
    <details onToggle={(e) => e.target.open && !data && adminApi.get(`/categories/${cat.id}/usage`).then((r) => setData(r.data)).catch((err) => toast.error(parseError(err).message))}>
      <summary className="cursor-pointer text-sm text-brand" data-testid={`category-usage-${cat.id}`}>{cat.inUse ? `Used by ${usageText(cat.usage)}` : "Not used"}</summary>
      {data && (
        <ul className="mt-2 space-y-1 text-sm" data-testid={`category-usage-list-${cat.id}`}>
          {data.records.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-2">
              <Link to={`/admin/${r.collection}/${r.id}`} className="font-semibold text-brand hover:underline">{r.title}</Link>
              <span className="text-xs text-ink-muted">{r.collection === "stories" ? "Story" : "Event"}</span><StatusBadge status={r.status} />
            </li>
          ))}
          {!data.records.length && <li className="text-ink-muted">No records use this.</li>}
        </ul>
      )}
    </details>
  );
};

const Row = ({ cat, siblings, reload }) => {
  const [name, setName] = useState(cat.name);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [target, setTarget] = useState("");

  const rename = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await adminApi.put(`/categories/${cat.id}`, { name });
      toast.success(cat.inUse ? `Renamed; ${cat.inUse} record(s) now show “${name.trim()}”` : "Renamed");
      setEditing(false);
      reload();
    } catch (err) {
      const p = parseError(err);
      setError(p.fields.name || p.message);
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.delete(`/categories/${cat.id}`, { params: target ? { reassignTo: target } : {} });
      toast.success(`Deleted “${data.deleted}”${data.reassigned ? `; ${data.reassigned} record(s) reassigned` : ""}`);
      reload();
    } catch (err) {
      toast.error(parseError(err).message);
    } finally {
      setBusy(false);
      setConfirm(false);
    }
  };

  return (
    <li className="space-y-2 py-4" data-testid={`category-row-${cat.id}`}>
      {editing ? (
        <form onSubmit={rename} className="flex flex-wrap items-start gap-2">
          <label htmlFor={`cat-${cat.id}`} className="sr-only">New name</label>
          <input id={`cat-${cat.id}`} value={name} onChange={(e) => setName(e.target.value)} className="field max-w-sm" aria-invalid={Boolean(error)} data-testid={`category-name-input-${cat.id}`} />
          <button type="submit" disabled={busy || !name.trim()} className="admin-btn" data-testid={`category-save-${cat.id}`}>{busy ? "Saving…" : "Save"}</button>
          <button type="button" onClick={() => { setEditing(false); setName(cat.name); setError(""); }} className="admin-btn-outline">Cancel</button>
          {error && <p role="alert" className="w-full text-sm font-semibold text-[#B42318]" data-testid={`category-error-${cat.id}`}>{error}</p>}
        </form>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-bold" data-testid={`category-name-${cat.id}`}>{cat.name}</span>
          <button type="button" onClick={() => setEditing(true)} className="text-sm font-bold text-brand underline" data-testid={`category-edit-${cat.id}`}>Edit</button>
          <button type="button" onClick={() => setConfirm(true)} className="text-sm font-bold text-[#B42318] underline" data-testid={`category-delete-${cat.id}`}>Delete</button>
        </div>
      )}
      <Usage cat={cat} />
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{cat.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              {cat.inUse ? `This is used by ${usageText(cat.usage)}. Choose where those records should move before deleting. No record will lose its classification.` : "No records use this. It will be removed from the list."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {cat.inUse > 0 && (
            <div className="space-y-2">
              <label htmlFor={`reassign-${cat.id}`} className="text-sm font-bold">Move records to</label>
              <select id={`reassign-${cat.id}`} value={target} onChange={(e) => setTarget(e.target.value)} className="field" data-testid={`category-reassign-${cat.id}`}>
                <option value="">Choose…</option>
                {siblings.filter((s) => s.id !== cat.id).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`category-delete-cancel-${cat.id}`}>Keep it</AlertDialogCancel>
            <AlertDialogAction disabled={busy || (cat.inUse > 0 && !target)} onClick={(e) => { e.preventDefault(); remove(); }} className="bg-[#B42318]" data-testid={`category-delete-confirm-${cat.id}`}>
              {cat.inUse ? "Reassign & delete" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </li>
  );
};

const AddForm = ({ kind, reload }) => {
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (busy || !name.trim()) return;
    setBusy(true);
    setError("");
    try {
      await adminApi.post("/categories", { kind, name });
      toast.success(`Added “${name.trim()}”`);
      setName("");
      reload();
    } catch (err) {
      const p = parseError(err);
      setError(p.fields.name || p.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="flex flex-wrap items-start gap-2">
      <label htmlFor={`add-${kind}`} className="sr-only">New name</label>
      <input id={`add-${kind}`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Add a new one" className="field max-w-sm" aria-invalid={Boolean(error)} data-testid={`category-add-input-${kind}`} />
      <button type="submit" disabled={busy || !name.trim()} className="admin-btn" data-testid={`category-add-${kind}`}>{busy ? "Adding…" : "Add"}</button>
      {error && <p role="alert" className="w-full text-sm font-semibold text-[#B42318]" data-testid={`category-add-error-${kind}`}>{error}</p>}
    </form>
  );
};

export default function Categories() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const reload = useCallback(() => {
    adminApi.get("/categories").then((r) => setData(r.data)).catch((e) => setError(parseError(e).message));
  }, []);
  useEffect(reload, [reload]);
  if (error) return <p role="alert" className="text-[#B42318]">{error}</p>;
  if (!data) return <p role="status" className="text-ink-muted">Loading categories…</p>;
  return (
    <div data-testid="admin-categories">
      <h1 className="text-3xl font-extrabold tracking-tight text-brand">Categories</h1>
      <p className="mt-2 max-w-2xl text-sm text-ink-muted">Renaming updates every story and event that uses the category. Categories still in use can only be deleted after their records are moved to another category.</p>
      <div className="mt-8 grid gap-8 xl:grid-cols-2">
        {KINDS.map(([kind, title, help]) => (
          <section key={kind} className="border border-line bg-white p-6" aria-labelledby={`h-${kind}`} data-testid={`categories-${kind}`}>
            <h2 id={`h-${kind}`} className="text-xl font-extrabold text-brand">{title} <span className="text-sm font-semibold text-ink-muted">({data[kind].length})</span></h2>
            <p className="mt-1 text-sm text-ink-muted">{help}</p>
            <div className="mt-4"><AddForm kind={kind} reload={reload} /></div>
            <ul className="mt-4 divide-y divide-line">{data[kind].map((c) => <Row key={`${c.id}-${c.name}`} cat={c} siblings={data[kind]} reload={reload} />)}</ul>
          </section>
        ))}
      </div>
    </div>
  );
}
